// Sand's thermal/entropy phase ladder: heat turns it molten, cooling turns
// molten glass to glass, draining Entropy turns glass to Quartz, and too
// much Entropy shatters Quartz back into sand. Structurally the odd one out
// among this session's three material-decay modules (rust.ts, candle.ts):
// rust's rustStage is a pure function of the object's current Entropy, read
// fresh every settle, no memory kept anywhere. That doesn't work here.
// `applyTransferEffect` never drains a `properties` value below zero (a
// drain's `actual` amount is capped at what's `available`), so Entropy is
// floored at 0 -- and "glass that has been drained all the way down to
// Quartz" and "sand that was never heated at all" would otherwise both read
// as the exact same (energy: 0, entropy: 0) with nothing live left to tell
// them apart. `SandDef.stage` is the memory that breaks the tie: not a
// cache of something derivable, but the one bit of real history ("has this
// been melted before") the Entropy floor makes otherwise unrecoverable.
//
// Each settle advances `stage` at most one step (see `nextSandStage`) --
// deliberately not resolved to a fixpoint within a single settle, the same
// restraint `settle.ts`'s own "deliberately not run to a fixpoint" note
// documents for the whole system list: a cast that (somehow) crosses two of
// these thresholds in one shot finishes advancing on the very next settle,
// which in practice is indistinguishable from immediate since almost
// nothing here is a single-cast action anyway (you cool it, *then*
// separately drain it).
import { ObjectDef, SandStageId } from './types';
import { PhysicsConfig, DEFAULT_PHYSICS } from './physics';
import { SimEvents } from './events';
import { WorldState } from './state';
import { StagePresentation, applyStagePresentation } from './presentation';

export interface SandStageMeta extends StagePresentation {
  /** Required here, unlike on `StagePresentation` itself: every stage of this
   * ladder is a different material -- sand, molten glass, glass, quartz -- and
   * three of the four were still labelled "Sand Block" while saying otherwise
   * on inspection (the 2026-08-11 report). */
  name: string;
}

/** Sand Block's own four-stage look and flavor text. */
export const SAND_BLOCK_META: Record<SandStageId, SandStageMeta> = {
  sand: {
    name: 'Sand Block',
    texture: 'object-block-sand',
    description: 'A block of packed, sun-baked sand, dense enough to hold its shape.',
  },
  molten: {
    name: 'Molten Glass',
    texture: 'object-block-glass-molten',
    description: 'A slumped mass of molten glass, glowing a dull orange-red. Fearsomely hot.',
  },
  glass: {
    name: 'Glass Block',
    texture: 'object-block-glass',
    description: 'A block of smooth, cooled glass, faintly translucent at the edges.',
  },
  quartz: {
    name: 'Quartz Block',
    texture: 'object-block-quartz',
    description: 'A block of glass, coaxed by cold into a lattice of clear quartz facets.',
  },
};

/** Sand Floor's own four-stage look and flavor text -- same ladder, walkable
 * terrain rather than a solid block. Molten glass underfoot has no hazard
 * mechanic yet (unlike Metal Floor's shock hazard) -- see sand.ts's own
 * header note in the diary/mechanics doc for what's deliberately deferred. */
export const SAND_FLOOR_META: Record<SandStageId, SandStageMeta> = {
  sand: {
    name: 'Sand Floor',
    texture: 'object-floor-sand',
    description: 'A section of floor, packed with fine, sun-baked sand.',
  },
  molten: {
    name: 'Molten Glass Floor',
    texture: 'object-floor-glass-molten',
    description: 'A section of floor, filled with slumped molten glass, glowing dull orange-red. Fearsomely hot underfoot.',
  },
  glass: {
    name: 'Glass Floor',
    texture: 'object-floor-glass',
    description: 'A section of floor, cooled to smooth, faintly translucent glass.',
  },
  quartz: {
    name: 'Quartz Floor',
    texture: 'object-floor-quartz',
    description: 'A section of floor, its glass drawn by cold into a lattice of clear quartz facets.',
  },
};

/** One stage-advance, given the object's current stage and its live Energy/
 * Entropy. Energy dominates unconditionally -- enough heat melts sand,
 * glass, or Quartz alike, always landing on 'molten' regardless of whatever
 * Entropy currently reads. Below `meltEnergy`, cooled 'molten' always lands
 * on 'glass' first (never straight to Quartz in one step, matching real
 * annealing: an uncontrolled cool makes glass, not crystal); 'glass' turns
 * to 'quartz' once Entropy is drained down to `crystalEntropy`; 'quartz'
 * breaks back to 'sand' once Entropy is pushed up past `shatterEntropy`.
 * Plain 'sand' has no Entropy-driven exit at all -- only heat moves it,
 * matching the sequence as described: nothing happens to raw sand by
 * touching Entropy alone. */
export function nextSandStage(current: SandStageId, energy: number, entropy: number, physics: PhysicsConfig = DEFAULT_PHYSICS): SandStageId {
  if (energy >= physics.sand.meltEnergy) return 'molten';
  if (current === 'molten') return 'glass';
  if (current === 'glass' && entropy <= physics.sand.crystalEntropy) return 'quartz';
  if (current === 'quartz' && entropy >= physics.sand.shatterEntropy) return 'sand';
  return current;
}

/** Advances `obj`'s stage by one step and re-derives its name, texture and
 * description to match -- see `presentation.ts`, which is where all three move
 * together for every ladder. Also the one place that
 * writes `properties.entropy`: while the *next* stage reads 'molten', pins
 * Entropy up to at least `glassEntropy` every settle it stays there -- the
 * real entropy increase a phase change causes, and the only reason cooling
 * later lands on a 'glass' that actually has something worth draining
 * toward Quartz. Idempotent (Math.max, not an overwrite), so it never fights
 * a player who has already pushed Entropy higher than that for some other
 * reason, and harmless to call every settle rather than only on the instant
 * it first crosses into 'molten'. */
export function refreshSandTexture(obj: ObjectDef, physics: PhysicsConfig, out: SimEvents): void {
  if (!obj.sand) return;
  const current = obj.sand.stage ?? 'sand';
  const energy = obj.properties?.energy ?? 0;
  const entropy = obj.properties?.entropy ?? 0;
  const next = nextSandStage(current, energy, entropy, physics);

  if (next === 'molten') {
    obj.properties = obj.properties ?? {};
    obj.properties.entropy = Math.max(entropy, physics.sand.glassEntropy);
  }
  if (next !== current) obj.sand.stage = next;

  // Molten glass is in thermal contact with whatever is standing on it, and
  // sand is not. Derived from the stage every settle, exactly like the texture
  // and the description above -- the cluster is not authoring here, it is one
  // more thing "which stage is this in" answers, so there is no way to author a
  // sand tile that looks molten and does not burn.
  //
  // `reciprocal`, so the heat is really leaving the glass: stand on a pool of
  // melt and you cool it, at the price of the Energy going into you. Enough of
  // that drops it under `meltEnergy` and it sets into glass under your feet,
  // which is the same cooling any other route would have done -- nothing here
  // knows a player is what did it.
  //
  // A Sand Block gets the same treatment and can never use it: nothing walks
  // onto a solid block, so no contact edge is ever collected. The rule belongs
  // to the material rather than to the silhouette, and stating it once for both
  // is what keeps a future walkable variant honest.
  if (next === 'molten') {
    obj.thermalContact = { conductance: physics.sand.moltenConductance, concept: 'energy', reciprocal: true };
  } else if (obj.thermalContact) {
    obj.thermalContact = undefined;
  }

  applyStagePresentation(obj, (obj.sand.variant === 'floor' ? SAND_FLOOR_META : SAND_BLOCK_META)[next], out);
}

/** The declared system's own body (see settle.ts): advances every sand
 * object one stage and re-derives its texture/description for whatever
 * just changed. */
export function settleSand(world: WorldState, out: SimEvents): void {
  const room = world.rooms[world.player.roomId];
  if (!room) return;
  for (const obj of room.objects) {
    if (obj.sand) refreshSandTexture(obj, world.physics, out);
  }
}
